import { createRng } from "@/lib/seededRandom";
import type { RevenueByDimension, RevenueMonth } from "@/types";
import { CLIENTS } from "./clients";
import { DEPARTMENT_NAMES } from "./constants";

const rng = createRng(552211);

export const CURRENT_MRR = CLIENTS.filter((c) => c.status === "active" || c.status === "at-risk").reduce(
  (sum, c) => sum + c.mrr,
  0,
);

export const MONTHLY_TARGET = Math.round(CURRENT_MRR * 1.06);

function monthLabel(offsetFromNow: number) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - offsetFromNow);
  return d.toISOString().slice(0, 7);
}

export const REVENUE_HISTORY: RevenueMonth[] = (() => {
  const months: RevenueMonth[] = [];
  let mrr = Math.round(CURRENT_MRR * 0.72);

  for (let i = 17; i >= 0; i--) {
    const isCurrent = i === 0;
    const newRevenue = rng.int(18, 62) * 100;
    const expansionRevenue = rng.int(10, 40) * 100;
    const lostRevenue = rng.int(6, 30) * 100;
    if (!isCurrent) {
      mrr = Math.max(10000, mrr + newRevenue + expansionRevenue - lostRevenue);
    } else {
      mrr = CURRENT_MRR;
    }
    months.push({
      month: monthLabel(i),
      mrr,
      target: Math.round(mrr * rng.float(0.97, 1.05)),
      newRevenue,
      expansionRevenue,
      lostRevenue,
      forecast: Math.round(mrr * rng.float(1.02, 1.08)),
      pipelineValue: Math.round(mrr * rng.float(0.4, 0.9)),
    });
  }

  for (let i = 1; i <= 3; i++) {
    const last = months[months.length - 1];
    const forecastMrr = Math.round(last.mrr * (1 + rng.float(0.015, 0.045)));
    months.push({
      month: monthLabel(-i),
      mrr: forecastMrr,
      target: Math.round(forecastMrr * 1.02),
      newRevenue: rng.int(20, 55) * 100,
      expansionRevenue: rng.int(12, 35) * 100,
      lostRevenue: rng.int(5, 22) * 100,
      forecast: forecastMrr,
      pipelineValue: Math.round(forecastMrr * rng.float(0.5, 0.95)),
    });
  }

  return months;
})();

export const PIPELINE_VALUE = REVENUE_HISTORY[17].pipelineValue;

function aggregateBy(keyFn: (c: (typeof CLIENTS)[number]) => string): RevenueByDimension[] {
  const map = new Map<string, number>();
  for (const c of CLIENTS) {
    if (c.status === "churned") continue;
    map.set(keyFn(c), (map.get(keyFn(c)) ?? 0) + c.mrr);
  }
  return [...map.entries()]
    .map(([label, value]) => ({ label, value, change: rng.float(-8, 18, 1) }))
    .sort((a, b) => b.value - a.value);
}

export const REVENUE_BY_AM = aggregateBy((c) => c.assignedAM).slice(0, 12);
export const REVENUE_BY_INDUSTRY = aggregateBy((c) => c.industry);

export const REVENUE_BY_DEPARTMENT: RevenueByDimension[] = (() => {
  const weights = [0.34, 0.05, 0.22, 0.24, 0.02, 0.01, 0.03, 0.02, 0.07];
  return DEPARTMENT_NAMES.map((name, i) => ({
    label: name,
    value: Math.round(CURRENT_MRR * weights[i] * rng.float(0.9, 1.1)),
    change: rng.float(-6, 14, 1),
  })).sort((a, b) => b.value - a.value);
})();

export const TOP_EXPANSION_OPPORTUNITIES = CLIENTS.filter((c) => c.expansionOpportunity)
  .sort((a, b) => (b.expansionValue ?? 0) - (a.expansionValue ?? 0))
  .slice(0, 10);

export const CHURN_RISK_CLIENTS = CLIENTS.filter(
  (c) => (c.riskLevel === "critical" || c.riskLevel === "high") && c.status !== "churned",
)
  .sort((a, b) => b.mrr - a.mrr)
  .slice(0, 12);
